// 数数科技 埋点
// https://docs-v2.thinkingdata.cn/?version=latest&lan=zh-CN&code=javascript_sdk_installation&anchorId=
// 管理后台：https://ta-web.cggame123.com/

import { Env } from '@/libs/env';
import { isBrowser } from './browser';
import { getDefaultStore } from 'jotai';
import { userInfoAtom, isLoginAtom } from '@/store';
import { balanceListAtom } from '@/store/balance';
import isEqual from 'lodash-es/isEqual';
import { isInApp } from '@/libs/comeAppSDK';
import { toNum } from '@/libs/decimal';

// 数数科技实例
let thinkingAnalytics: any = null;
let initPromise: Promise<void> | null = null;
let isInitializing = false;
// 为初始化成功前，缓存事件
const eventQueue: { event: string; properties?: Record<string, any>; }[] = [];
// 当前用户ID状态
let currentDistinctId: string | null = null;
// 用户是否已设置
let isUserSetup = false;
// 用户状态监听是否已启动
let isUserTrackingStarted = false;
// 公共属性
let commonProps: Record<string, any> = {};

// 埋点事件类型定义
export interface TrackEvent {
  event: string;
  properties?: Record<string, any>;
  distinctId?: string;
  time?: Date;
}

// withdraw_request 埋点 source 字段类型
export type WithdrawRequestSource =
  | 'home_page_top_login'
  | 'home_page_top_unlogin'
  | 'home_page_casino'
  | 'home_page_rummy'
  | 'home_page_fantasy'
  | 'home_page_sports'
  | 'home_page_bottom'
  | 'casino_banner_top'
  | 'casino_banner_two'
  | 'casino_banner_three'
  | 'casino_banner_four';


// 用户属性类型定义
export interface UserProperties {
  [key: string]: any;
}

/**
 * 获取公共事件属性
 * 包含VIP等级、余额等公共属性
 */
const getCommonProperties = (): Record<string, any> => {
  if (!isBrowser()) {
    return {};
  }

  const store = getDefaultStore();
  const balanceData = store.get(balanceListAtom);
  // 余额和VIP信息
  if (balanceData) {
    commonProps.current_vip_level = toNum(balanceData.vip_level || 0);
    commonProps.current_deposit_amount = toNum(balanceData.deposit_balance || '0');
    commonProps.current_bonus_amount = toNum(balanceData.bonus_balance || '0');
    commonProps.current_withdraw_amount = toNum(balanceData.withdraw_balance || '0');
  }

  return commonProps;
};

/**
 * 刷新公共属性
 * @param params 
 */
export const refreshCommonProperties = (params: Record<string, any>) => {
  // 需要对比，没有变化不更新
  if (isEqual(commonProps, params)) {
    return;
  }
  commonProps = {
    ...commonProps,
    ...params,
  };
};

/**
 * 发送埋点事件
 * @param event 事件名称
 * @param properties 事件属性
 * @example
 * track('register_login_entrance_click', 
 *  {
      current_page: 'https://www.google.com',
      current_module: 'Home',
    }
  });
 */
export const track = async (event: string, properties?: Record<string, any>) => {
  if (!isBrowser()) {
    return;
  }

  // 确保初始化完成
  await ensureInitialized();

  if (!thinkingAnalytics) {
    eventQueue.push({ event, properties });
    return;
  }

  try {
    const finalProperties = {
      ...commonProps,
      ...properties,
    };
    // 本地开发环境不埋点
    // if (Env.NEXT_PUBLIC_ENV_NAME === 'dev') {
    //   return;
    // }
    thinkingAnalytics.track(event, finalProperties);
  } catch (error) {
    console.error('track failed:', error);
  }
};

/**
 * 初始化数数科技埋点
 */
export const initTrack = async () => {
  if (!isBrowser()) {
    return;
  }
  if (thinkingAnalytics || isInitializing) {
    return;
  }
  isInitializing = true;
  try {
    // onerror 脚本在 CDN 故障时提前加载并初始化了 TA SDK（window.__taPreloaded），直接复用实例
    // 好处：避免双初始化，同时 pageView 已由 onerror 阶段统计，此处跳过
    // 从 URL 读取 traceId（APK 新包会携带）；H5 页面自动生成 h5_ 前缀的唯一标识
    const traceId = new URLSearchParams(window.location.search).get('traceId')
      ?? `h5_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    commonProps.trace_id = traceId;
    commonProps.h5_version = Env.RELEASE;

    if ((window as any).__taPreloaded && (window as any).thinkingdata) {
      thinkingAnalytics = (window as any).thinkingdata;
    } else {
      // 动态导入数数科技 SDK，避免 SSR 问题
      const thinkingdata = await import('thinkingdata-browser');

      // 初始化数数科技 SDK
      thinkingdata.default.init({
        appId: Env.NEXT_PUBLIC_THINKINGDATA_APP_ID,
        serverUrl: Env.NEXT_PUBLIC_THINKINGDATA_SERVER_URL,
        useAppTrack: !!isInApp(),
        // 是否开启调试模式（开发环境）
        // debug: Env.NODE_ENV === 'development',
        // 是否开启日志
        showLog: !['pre', 'production'].includes(Env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || ""),
        // 自动收集页面浏览事件
        autoTrack: {
          pageView: true,
          // click: true,
          // change: true,
          // submit: true,
        },
      });

      thinkingAnalytics = thinkingdata.default;
    }

    // 初始化完成后，设置用户ID
    await setupUserTracking();

    // apk 未登录才需要设置
    if (isInApp() && !localStorage.getItem('accessToken')) {
      thinkingAnalytics.identify(localStorage.getItem('deviceId'));
    }
    // 启动用户状态监听（只启动一次）
    if (!isUserTrackingStarted) {
      startUserTracking();
      isUserTrackingStarted = true;
    }

    // 补发上次白屏/崩溃未发出的 webview_page_finished（is_retry 标记）
    try {
      const retryTime = localStorage.getItem('__wvpf_retry');
      if (retryTime) {
        thinkingAnalytics.track('webview_page_finished', { is_retry: true }, new Date(retryTime));
        localStorage.removeItem('__wvpf_retry');
      }
    } catch (e) {
      console.warn('wvpf retry failed:', e);
    }

    // 处理 head 阶段队列（如 webview_page_finished），用原始时间戳回填
    // 独立 try/catch 防止 SDK 不支持第三参数时中断后续 eventQueue 处理
    try {
      const headQueue = (window as any).__headTrackQueue as Array<{ event: string; time: Date; }> | undefined;
      if (Array.isArray(headQueue)) {
        while (headQueue.length > 0) {
          const item = headQueue.shift()!;
          if (item?.event) {
            thinkingAnalytics.track(item.event, {}, item.time);
          }
        }
      }
      // 当前 session 正常消费，清除 pending 标记
      localStorage.removeItem('__wvpf');
    } catch (e) {
      console.warn('headTrackQueue drain failed:', e);
    }

    // Process queued events
    while (eventQueue.length > 0) {
      const { event, properties } = eventQueue.shift()!;
      thinkingAnalytics.track(event, properties || {});
    }
  } catch (error) {
    console.error('ThinkingData init failed:', error);
  } finally {
    isInitializing = false;
  }
};

/**
 * 设置用户属性
 * @param properties 用户属性
 */
export const setUserProperties = (properties: UserProperties) => {
  if (!thinkingAnalytics) {
    return;
  }

  try {
    thinkingAnalytics.userSet(properties);
  } catch (error) {
    console.error('Failed to set user properties:', error);
  }
};

/**
 * 获取数数科技实例（用于高级用法）
 */
export const getThinkingAnalytics = () => {
  return thinkingAnalytics;
};

/**
 * 获取访客ID
 * @returns 访客ID，如果未初始化则返回 null
 * @example
 * const distinctId = getDistinctId();
 */
export const getDistinctId = (): string | null => {
  if (!isBrowser() || !thinkingAnalytics) {
    return null;
  }

  try {
    // apk内，distinctId 使用 deviceId 代替(服务端接口需要用)
    if (isInApp()) {
      return localStorage.getItem('deviceId') || '';
    }
    return thinkingAnalytics.getDistinctId();
  } catch (error) {
    console.error('Failed to get distinctId:', error);
    return null;
  }
};

export const ensureInitialized = () => {
  if (!initPromise) {
    initPromise = initTrack();
  }
  return initPromise;
};

/**
 * 设置用户ID（登录/未登录状态）
 * @param distinctId 用户ID
 * @param isLogin 是否登录状态
 */
export const setDistinctId = async (distinctId: string) => {
  if (!thinkingAnalytics || !distinctId) {
    return;
  }

  // 如果用户ID没有变化，不需要重新设置
  if (currentDistinctId === distinctId && isUserSetup) {
    return;
  }

  try {
    // 登录状态：使用 login 方法
    thinkingAnalytics.login(distinctId);

    currentDistinctId = distinctId;
    isUserSetup = true;
  } catch (error) {
    console.error('Failed to set distinctId:', error);
  }
};

/**
 * logout
 */
export const thinkingLogout = () => {
  if (!thinkingAnalytics) {
    return;
  }
  thinkingAnalytics.logout();
};

/**
 * 根据当前状态自动设置用户ID
 */
const setupUserTracking = async () => {
  if (!isBrowser()) {
    return;
  }

  const store = getDefaultStore();
  const isLogin = store.get(isLoginAtom);
  const userInfo = store.get(userInfoAtom);

  let distinctId: string | null = null;

  if (isLogin && userInfo?.id) {
    // 登录状态：使用用户ID
    distinctId = userInfo.id.toString();
  }

  if (distinctId) {
    await setDistinctId(distinctId);
  }
};

/**
 * 监听用户状态变化并自动设置用户ID
 */
export const startUserTracking = () => {
  if (!isBrowser()) {
    return;
  }

  const store = getDefaultStore();

  // 监听登录状态变化
  store.sub(isLoginAtom, () => {
    setupUserTracking();
  });

  // 监听用户信息变化
  store.sub(userInfoAtom, () => {
    setupUserTracking();
  });

  // 监听余额数据变化
  store.sub(balanceListAtom, () => {
    getCommonProperties();
  });

  // 初始设置
  setupUserTracking();
  getCommonProperties();
};